import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';
import {
  ApplicationName,
  FormNavigation,
  ControlInputGroup
} from 'components/CASAForm';
import {
  AuCallout,
  AuFormItemWrapper,
  AuSelect,
  AuTextInput,
  AuDirectionLink,
  AuDateInput
} from 'components/DTA';
import { Loader } from 'components/common';
import { FormContext } from 'contexts';
import { ArnContext } from 'contexts';
import { useFormikContext } from 'formik';
import { ArnApplicationDvsMethodFormData } from 'types/forms/arnApplicationTypes';
import { IFormAlertType } from 'types/forms/formTypes';
import { TITLES } from 'arnConstants';
import { getIdentityDocumentTitleAndDataModel } from 'helpers/arnApplicationHelper';

const PersonalDetails: React.FC = () => {
  const { values, validateForm, getFieldMeta } = useFormikContext<ArnApplicationDvsMethodFormData>();
  const { setFormAlert, openStepById, setOnChangeValidation } = useContext(FormContext);
  const { referenceDataResponse } = useContext(ArnContext);
  const history = useHistory();
  let isLocked = false;
  if (values.identityType) {
    const identityType = getIdentityDocumentTitleAndDataModel(values.identityType);
    const dvsValidationIndicator = getFieldMeta(`${identityType.identityType}.dvsValidationIndicator`).value;
    isLocked = dvsValidationIndicator === 'Passed';
  }

  const handleNext = async () => {
    // Run step validation
    const validationErrors = await validateForm();
    // Proceed to DeclarationAndSubmit if there aren't any errors
    if (!validationErrors || isEmpty(validationErrors)) {
      setFormAlert(null);
      setOnChangeValidation(false);
      openStepById('AddressAndContact');
    }
    else {
      //TODO: will be verified later once the BA finalise the validation rules and message requirments
      const alert: IFormAlertType = {
        as: 'error',
        title: 'There was a problem with the form',
        message: 'Please check the form below and try again'
      };

      setFormAlert(alert);
      setOnChangeValidation(true);
    }
  };

  const handleBack = () => {
    history.goBack();
  };

  //TODO: filtering will not be required once this is implemented in new API endpoint
  // map to get the country name and code only
  // remove duplication and then sort alphabetically
  const countryList = referenceDataResponse?.value && referenceDataResponse?.value['COUNTRY']
    .map(country => {
      return {
        name: country.casa_name,
        code: country.casa_code
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((country, index, self) =>
      index === self.findIndex(value => value.code === country.code)
    );

  const nationality = referenceDataResponse?.value && referenceDataResponse?.value['NATIONALITY']
    .map(nationality => {
      return {
        name: nationality.casa_name
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((nationality, index, self) =>
      index === self.findIndex(value => value.name === nationality.name)
    );

  if (!countryList || countryList.length <= 0) {
    return <Loader message='Working. Please wait.....' />;
  }

  return (
    <>
      <ApplicationName name='Personal details' />
      <AuDirectionLink
        onClick={handleBack}
        text="Back"
        direction="left" />
      <AuFormItemWrapper>
        <AuCallout >
          <p>Note: Your name must be your <strong>current, full legal given and family names </strong>
            which must match your identification unless your legal name has changed.
          </p>
        </AuCallout>
      </AuFormItemWrapper>
      <AuSelect
        name='title'
        label='Title'
        block
        options={[
          {
            text: 'Please select',
            value: ''
          },
          ...TITLES.map(title => {
            return {
              text: title,
              value: title
            };
          })
        ]} />
      <AuTextInput
        name='firstName'
        label='Legal given first name'
        as='input'
        block />
      <AuTextInput
        name='otherGivenNames'
        label='Legal other given names'
        as='input'
        block />
      <AuTextInput
        name='lastName'
        label='Legal family name'
        as='input'
        block />
      <AuDateInput
        name='dateOfBirth'
        label='Date of birth'
        hintText='For example, 31 01 1990'
        disabled={isLocked} />
      <ControlInputGroup
        name='sex'
        label='Sex'
        hintText='We require information on an applicant&apos;s sex to inform medical questionnaires as part of flight crew licence applications'
        type='radio'
        block
        options={[
          {
            label: 'Male',
            value: 'male',
            id: 'radio-personalDetails-sex-male'
          },
          {
            label: 'Female',
            value: 'female',
            id: 'radio-personalDetails-sex-female'
          },
          {
            label: 'Other',
            value: 'other',
            id: 'radio-personalDetails-sex-other'
          }
        ]}
      />
      {
        nationality && nationality.length > 0 && <AuSelect
          name={'nationality'}
          label='Nationality'
          block
          options={[
            ...nationality.map((nationality: { name: string; }) => {
              return {
                text: nationality.name,
                value: nationality.name
              };
            })
          ]}
        />
      }
      <FormNavigation
        nextButtonText='Next'
        onNextAction={handleNext}
        hidePrevious
        showSaveAndExit={false} />
    </>
  );
};

export default PersonalDetails;